import React, { useState, useEffect, useRef } from 'react';
import { getArtformContent } from '../utils/artformService';
import { buildStepperStepList } from '../utils/artformDetailSections';

/**
 * ArtformAboutStepper Component
 * Displays a compact, sequential "About This Artform" experience.
 * Shows one step at a time with a clear "Next" flow.
 */
const ArtformAboutStepper = ({ artformKey, onClose, onComplete, artistName, artworkTitle, movementName, artworkImageUrl }) => {
  const [artformData, setArtformData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [introDone, setIntroDone] = useState(false);
  const [showHotspots, setShowHotspots] = useState(false);
  const stepRefs = useRef({});

  const handleBack = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
      setTimeout(() => {
        if (stepRefs.current.container) {
          stepRefs.current.container.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!artformKey) {
        console.warn('[ArtformAboutStepper] No artform key provided');
        setError('No artform key provided');
        setLoading(false);
        return;
      }

      console.log('[ArtformAboutStepper] Fetching artform content for key:', artformKey);
      try {
        setLoading(true);
        setError(null);
        const data = await getArtformContent(artformKey);
        
        console.log('[ArtformAboutStepper] Received data:', data ? 'Data received' : 'No data', data);
        
        if (!data) {
          console.warn('[ArtformAboutStepper] Artform content not found for key:', artformKey);
          setError('Artform content not found');
          setLoading(false);
          return;
        }

        console.log('[ArtformAboutStepper] Setting artform data:', Object.keys(data));
        setArtformData(data);
      } catch (err) {
        console.error('[ArtformAboutStepper] Error fetching artform content:', err);
        setError('Failed to load artform content. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [artformKey]);

  const handleNext = () => {
    if (!steps || steps.length === 0) return;

    // Move to next learning step
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
      // Smooth scroll to top of container
      setTimeout(() => {
        if (stepRefs.current.container) {
          stepRefs.current.container.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
      }, 100);
      return;
    }

    // Final step: mark intro as complete and notify parent
    setIntroDone(true);
    if (onComplete) {
      onComplete();
    }
  };

  const steps = artformData ? buildStepperStepList(artformData) : [];
  const totalSteps = steps.length;
  const currentStep = steps[currentStepIndex];
  const progressPercentage = totalSteps > 0 ? ((currentStepIndex + 1) / totalSteps) * 100 : 0;
  const isLastStep = currentStepIndex === totalSteps - 1;

  // Loading state
  if (loading) {
    return (
      <div className="w-full p-4 md:p-6">
        <div className="space-y-4">
          {[1, 2, 3, 4].map(i => (
            <div
              key={i}
              className="bg-gradient-to-br from-blue-50 to-yellow-50 rounded-xl p-4 md:p-6 border-2 border-blue-100 shadow-sm animate-pulse"
            >
              <div className="h-6 bg-blue-200/50 rounded w-3/4 mb-3"></div>
              <div className="h-4 bg-blue-200/30 rounded w-full mb-2"></div>
              <div className="h-4 bg-blue-200/30 rounded w-5/6"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error || !artformData) {
    return (
      <div className="w-full p-4 md:p-6">
        <div className="bg-gradient-to-br from-pink-50 to-yellow-50 rounded-xl p-6 md:p-8 border-2 border-pink-200 shadow-lg text-center">
          <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-3">
            {error || 'Content Coming Soon!'}
          </h3>
          <p className="text-gray-600 mb-4">
            We're working on adding more information about this artform. Check back soon!
          </p>
          {onClose && (
            <button
              onClick={onClose}
              className="px-6 py-2 bg-blue-200 text-gray-800 rounded-lg hover:bg-blue-300 transition-colors text-sm font-medium"
            >
              Close
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!currentStep) {
    return null;
  }

  // When intro is done, show a simple transition message instead of the stepper
  if (introDone) {
    return (
      <div className="w-full px-4 py-3 md:px-6 md:py-4">
        <div className="bg-gradient-to-r from-amber-50 via-yellow-50 to-amber-100 border-2 border-amber-200 rounded-xl md:rounded-2xl shadow-md text-center">
          <p className="text-sm md:text-base text-amber-900 font-semibold px-4 py-3">
            Now let's learn about this artwork by {artistName || 'the artist'} in the {movementName || artformData?.title || 'art movement'} style.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Artist, Artwork, and Style Introduction - Simple and Kid-Friendly */}
      <div className="mb-5 px-4">
        <div className="bg-white rounded-xl p-4 md:p-5 border border-gray-200 shadow-sm">
          <div className="mb-3">
            <p className="text-base md:text-lg text-black font-bold mb-2">
              {artworkTitle && artistName && (
                <>
                  {artworkTitle} by {artistName}
                </>
              )}
              {!artworkTitle && artistName && (
                <>{artistName}</>
              )}
            </p>
            <div className="flex items-center gap-2 flex-wrap mb-3">
              <span className="text-xs md:text-sm text-gray-600">Style:</span>
              <span className="text-sm md:text-base font-semibold text-black">
                {movementName || artformData?.title || 'Art Movement'}
              </span>
              {artformData?.era && (
                <span className="text-xs text-gray-500">({artformData.era})</span>
              )}
            </div>
            <p className="text-sm md:text-base text-black leading-relaxed">
              <span className="font-semibold">{artistName || 'The artist'}</span> used <span className="font-semibold">"{movementName || artformData?.title || 'this art style'}"</span> to create the artwork. Let's learn more about <span className="font-semibold">"{movementName || artformData?.title || 'this art style'}"</span>.
            </p>
          </div>
        </div>
      </div>

      {/* Two-Column Layout: Artwork Left, Steps Right */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-4 md:gap-6 px-4">
        {/* LEFT: Artwork Image */}
        <div className="flex flex-col">
          {artworkImageUrl && (
            <div className="mb-4">
              <div className="rounded-xl overflow-hidden border-2 border-gray-200 shadow-md bg-white">
                <img 
                  src={artworkImageUrl} 
                  alt={artworkTitle || 'Artwork'} 
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>
          )}
          
          {/* Explore Artwork Button - Below the artwork */}
          <button
            onClick={() => {
              setShowHotspots(true);
              if (onComplete) {
                onComplete();
              }
            }}
            className="w-full bg-gradient-to-r from-amber-400 to-orange-400 hover:from-amber-500 hover:to-orange-500 text-white text-sm md:text-base font-semibold py-2.5 px-4 rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
          >
            Explore artwork learning →
          </button>
        </div>

        {/* RIGHT: Progress and Steps */}
        <div className="flex flex-col">
          {/* Compact Progress Indicator */}
          {totalSteps > 0 && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-gray-600 font-medium">
                  {currentStepIndex + 1} of {totalSteps} learnings
                </span>
                <span className="text-xs text-gray-500">
                  {Math.round(progressPercentage)}%
                </span>
              </div>
              <div className="w-full bg-amber-100 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-amber-400 via-amber-500 to-orange-400 h-full rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>
          )}

          {/* Single Step Display - Sequential */}
          <div 
            ref={el => stepRefs.current.container = el}
            className="flex-1"
          >
            <div className="bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 rounded-xl border-2 border-amber-200 shadow-lg p-5 md:p-6 relative min-h-[300px]">
              {/* Step Number Badge */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-400 flex items-center justify-center font-bold text-white text-sm shadow-md">
                    {currentStepIndex + 1}
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-gray-800">
                    {currentStep.label}
                  </h3>
                </div>
              </div>

              {/* Step Content */}
              <div className="mb-12 md:mb-14 pr-16 md:pr-20">
                {currentStep.type === 'artists' && Array.isArray(currentStep.body) ? (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-700 mb-3 font-medium">
                      Artists who worked in this style:
                    </p>
                    <ul className="list-disc list-inside space-y-2 text-sm md:text-base text-gray-700">
                      {currentStep.body.map((artist, idx) => (
                        <li key={idx} className="leading-relaxed">
                          {artist}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : currentStep.type === 'visual-clues' && Array.isArray(currentStep.body) ? (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-700 mb-3 font-medium">
                      Try spotting these in artworks from this style:
                    </p>
                    <ul className="list-disc list-inside space-y-2 text-sm md:text-base text-gray-700">
                      {currentStep.body.map((line, idx) => (
                        <li key={idx} className="leading-relaxed">
                          {line}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : currentStep.type === 'famous-works' && Array.isArray(currentStep.body) ? (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-700 mb-3 font-medium">
                      Famous artworks in this style:
                    </p>
                    {currentStep.body.map((work, idx) => (
                      <div
                        key={idx}
                        className="bg-white/80 rounded-lg p-3 border border-amber-200 shadow-sm"
                      >
                        <p className="font-semibold text-gray-800 text-sm md:text-base">
                          {work.title || 'Untitled'}
                        </p>
                        {work.artist && (
                          <p className="text-gray-600 text-xs md:text-sm mt-1">
                            by {work.artist}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm md:text-base text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {currentStep.body}
                  </p>
                )}
              </div>

              {/* Navigation Buttons - Back on left bottom, Next on right bottom */}
              <div className="absolute bottom-4 left-4 right-4 flex justify-between items-center">
                <div>
                  {currentStepIndex > 0 && (
                    <button
                      onClick={handleBack}
                      className="bg-white border-2 border-amber-300 text-amber-700 text-xs font-medium py-1.5 px-3 rounded-md shadow-sm hover:shadow-md hover:bg-amber-50 transition-all duration-200"
                    >
                      ← Back
                    </button>
                  )}
                </div>
                <button
                  onClick={handleNext}
                  className="bg-gradient-to-r from-amber-400 to-orange-400 hover:from-amber-500 hover:to-orange-500 text-white text-xs font-medium py-1.5 px-3 rounded-md shadow-sm hover:shadow-md transition-all duration-200"
                >
                  {isLastStep ? 'Start →' : 'Next →'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArtformAboutStepper;
